import React from 'react';
import { Helmet } from 'react-helmet-async';

const Analytics: React.FC = () => {
  // Mock data for analytics
  const analyticsData = {
    totalVisitors: 15420,
    uniqueVisitors: 8920,
    pageViews: 45680,
    bounceRate: 23.4,
    avgSessionDuration: '4m 32s',
    conversionRate: 3.2,
    topPages: [
      { page: '/home', views: 12500, conversion: 4.2 },
      { page: '/products', views: 8900, conversion: 2.8 },
      { page: '/about', views: 6700, conversion: 1.5 },
      { page: '/contact', views: 4500, conversion: 3.1 }
    ],
    trafficSources: [
      { source: 'Organic Search', percentage: 45, visitors: 6939 },
      { source: 'Direct', percentage: 28, visitors: 4318 },
      { source: 'Social Media', percentage: 15, visitors: 2313 },
      { source: 'Referral', percentage: 12, visitors: 1850 }
    ],
    monthlyTrends: [
      { month: 'Jan', visitors: 12000, conversions: 384 },
      { month: 'Feb', visitors: 13500, conversions: 432 },
      { month: 'Mar', visitors: 14200, conversions: 454 },
      { month: 'Apr', visitors: 15420, conversions: 493 }
    ]
  };

  return (
    <>
      <Helmet>
        <title>AI Traffic Analytics - Dream Digi Platform</title>
      </Helmet>
      
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Traffic Analytics</h1>
          <p className="text-gray-600">Comprehensive insights into your website's performance and visitor behavior</p>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Visitors</p>
                <p className="text-2xl font-bold text-gray-900">{analyticsData.totalVisitors.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <i className="fas fa-users text-blue-600 text-xl"></i>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-green-600 text-sm font-medium">+12.5%</span>
              <span className="text-gray-500 text-sm ml-1">from last month</span>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Unique Visitors</p>
                <p className="text-2xl font-bold text-gray-900">{analyticsData.uniqueVisitors.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <i className="fas fa-user-friends text-green-600 text-xl"></i>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-green-600 text-sm font-medium">+8.3%</span>
              <span className="text-gray-500 text-sm ml-1">from last month</span>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Page Views</p>
                <p className="text-2xl font-bold text-gray-900">{analyticsData.pageViews.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <i className="fas fa-eye text-purple-600 text-xl"></i>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-green-600 text-sm font-medium">+15.2%</span>
              <span className="text-gray-500 text-sm ml-1">from last month</span>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                <p className="text-2xl font-bold text-gray-900">{analyticsData.conversionRate}%</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <i className="fas fa-chart-line text-orange-600 text-xl"></i>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-green-600 text-sm font-medium">+0.8%</span>
              <span className="text-gray-500 text-sm ml-1">from last month</span>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Monthly Trends Chart */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Trends</h3>
            <div className="space-y-4">
              {analyticsData.monthlyTrends.map((trend, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">{trend.month}</span>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{trend.visitors.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">visitors</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{trend.conversions}</p>
                      <p className="text-xs text-gray-500">conversions</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Traffic Sources */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Traffic Sources</h3>
            <div className="space-y-4">
              {analyticsData.trafficSources.map((source, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      index === 0 ? 'bg-blue-500' : 
                      index === 1 ? 'bg-green-500' : 
                      index === 2 ? 'bg-purple-500' : 'bg-orange-500'
                    }`}></div>
                    <span className="text-sm font-medium text-gray-900">{source.source}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{source.percentage}%</p>
                    <p className="text-xs text-gray-500">{source.visitors.toLocaleString()} visitors</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Performing Pages */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Pages</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page Views</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversion Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {analyticsData.topPages.map((page, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{page.page}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{page.views.toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{page.conversion}%</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        page.conversion > 3 ? 'bg-green-100 text-green-800' : 
                        page.conversion > 2 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {page.conversion > 3 ? 'Excellent' : page.conversion > 2 ? 'Good' : 'Needs Improvement'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* AI Insights */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <i className="fas fa-robot text-blue-600 mr-2"></i>
            AI Insights & Recommendations
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <h4 className="font-medium text-gray-900 mb-2">Performance Optimization</h4>
              <p className="text-sm text-gray-600">Your homepage has excellent conversion rates. Consider A/B testing different CTAs to improve further.</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <h4 className="font-medium text-gray-900 mb-2">SEO Opportunities</h4>
              <p className="text-sm text-gray-600">45% of traffic comes from organic search. Focus on long-tail keywords for better rankings.</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <h4 className="font-medium text-gray-900 mb-2">User Experience</h4>
              <p className="text-sm text-gray-600">Bounce rate is below industry average. Consider adding more engaging content to product pages.</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <h4 className="font-medium text-gray-900 mb-2">Mobile Performance</h4>
              <p className="text-sm text-gray-600">Mobile users account for 68% of traffic. Ensure mobile optimization is prioritized.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Analytics;
